'use client';
import React from 'react';

const services = [
  {
    icon: '🏙️',
    title: 'Private Morocco Tours',
    description:
      'Fully customized private tours across Morocco\'s imperial cities, coastal towns, and hidden gems. Travel at your own pace with exclusive access and personal attention.',
    features: ['Fully private', 'Custom itinerary', 'Luxury vehicle', 'Expert guide'],
  },
  {
    icon: '✡️',
    title: 'Jewish Heritage Tours',
    description:
      'Immersive journeys through Morocco\'s remarkable Jewish history. Visit synagogues, mellahs, cemeteries, rabbinical shrines, and historic Jewish communities.',
    features: ['Synagogues & mellahs', 'Genealogy research', 'Kosher-friendly', 'Expert commentary'],
    featured: true,
  },
  {
    icon: '🏙️',
    title: 'Casablanca Tours',
    description:
      'Explore Morocco\'s vibrant economic capital — from the magnificent Hassan II Mosque to the historic Mellah, Art Deco architecture, and modern city highlights.',
    features: ['Hassan II Mosque', 'Mellah quarter', 'Art Deco tour', 'Corniche & marina'],
  },
  {
    icon: '🌅',
    title: 'Day Trips',
    description:
      'Perfect single-day excursions from any Moroccan city. Discover nearby destinations, historical sites, and natural wonders with a knowledgeable private guide.',
    features: ['Flexible timing', 'Hotel pickup', 'All-inclusive', 'Multiple routes'],
  },
  {
    icon: '🚗',
    title: 'Luxury Chauffeur Service',
    description:
      'Travel in style and comfort with our premium chauffeur service. Modern, air-conditioned luxury vehicles driven by a professional, discreet driver.',
    features: ['Mercedes & premium cars', 'Professional driver', 'Door-to-door', 'Available 24/7'],
  },
  {
    icon: '✈️',
    title: 'Airport Transfers',
    description:
      'Stress-free airport transfers to and from all major Moroccan airports. Meet & greet service, flight monitoring, and comfortable luxury vehicles.',
    features: ['All airports', 'Flight tracking', 'Meet & greet', 'Fixed pricing'],
  },
  {
    icon: '🚢',
    title: 'Cruise Shore Excursions',
    description:
      'Make the most of your port stop with expertly guided shore excursions. We ensure you return to your ship on time while experiencing the best of Morocco.',
    features: ['All Moroccan ports', 'Guaranteed return', 'Private groups', 'Flexible duration'],
  },
  {
    icon: '🗺️',
    title: 'Custom Tours',
    description:
      'Your dream Morocco itinerary, designed entirely around your interests, schedule, and preferences. Every detail crafted to perfection for an unforgettable journey.',
    features: ['Bespoke planning', 'Any duration', 'Special occasions', 'Multi-city routes'],
  },
  {
    icon: '🐪',
    title: 'Sahara Desert Experience',
    description:
      'Authentic camel trekking in the Sahara Desert. Sunrise and sunset rides, overnight desert camps, and stargazing under the Moroccan sky.',
    features: ['Erg Chebbi Dunes', 'Camel trekking', 'Desert camps', 'Stargazing'],
  },
  {
    icon: '🚐',
    title: 'Private Transportation',
    description:
      'Comfortable intercity transfers between all Moroccan cities. Travel between Casablanca, Marrakech, Fes, Rabat, and beyond in air-conditioned luxury vehicles.',
    features: ['All cities', 'Intercity routes', 'Group capacity', 'Luggage assistance'],
  },
  {
    icon: '👑',
    title: 'VIP Travel',
    description:
      'The ultimate luxury Morocco experience. VIP treatment from arrival to departure — exclusive access, premium accommodations, private dining, and bespoke experiences.',
    features: ['VIP access', 'Premium hotels', 'Private dining', 'Concierge service'],
  },
  {
    icon: '🌿',
    title: 'Wellness & Cultural Tours',
    description:
      'Discover Morocco\'s rich traditions of hammam culture, Argan oil heritage, traditional cuisine, and artisan crafts through immersive private cultural experiences.',
    features: ['Hammam visits', 'Cooking classes', 'Artisan workshops', 'Culinary tours'],
  },
];

export default function Services() {
  const handleNavClick = (href: string) => {
    const el = document.querySelector(href);
    if (el) {
      const offset = 80;
      const top = el.getBoundingClientRect().top + window.scrollY - offset;
      window.scrollTo({ top, behavior: 'smooth' });
    }
  };

  return (
    <section id="services" className="section-padding bg-white">
      <div className="container-luxury">
        {/* Header */}
        <div className="text-center mb-16 animate-on-scroll">
          <span className="tag-gold">Our Services</span>
          <h2 className="section-title mt-4 mb-4">
            Luxury Travel Experiences
            <br />
            <span className="text-gold italic">Tailored for You</span>
          </h2>
          <div className="gold-divider" />
          <p className="section-subtitle mx-auto mt-4">
            From private city tours to Jewish heritage journeys, airport transfers to desert adventures
            — every experience is crafted exclusively for you.
          </p>
        </div>

        {/* Services Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
          {services.map((service, index) => (
            <div
              key={service.title}
              className={`service-card relative ${
                service.featured ? 'border-2 border-gold' : 'border border-gray-100'
              } animate-on-scroll`}
              style={{ transitionDelay: `${(index % 3) * 100}ms` }}
            >
              {service.featured && (
                <div className="absolute -top-3 left-6">
                  <span className="font-sans text-xs font-bold uppercase tracking-wider px-3 py-1 bg-gold text-navy-dark">
                    Most Popular
                  </span>
                </div>
              )}

              {/* Icon */}
              <div className="w-14 h-14 bg-cream flex items-center justify-center text-2xl mb-5 group-hover:bg-gold/10 transition-colors duration-300">
                {service.icon}
              </div>

              {/* Title */}
              <h3 className="font-serif text-xl font-bold text-navy mb-3 group-hover:text-gold transition-colors duration-300">
                {service.title}
              </h3>

              {/* Divider */}
              <div className="w-8 h-0.5 bg-gold mb-4 group-hover:w-16 transition-all duration-300" />

              {/* Description */}
              <p className="font-sans text-sm text-gray-600 leading-relaxed mb-5">
                {service.description}
              </p>

              {/* Features */}
              <ul className="space-y-1.5 mb-6">
                {service.features.map((feature) => (
                  <li key={feature} className="flex items-center gap-2 font-sans text-xs text-gray-500">
                    <div className="w-1.5 h-1.5 bg-gold flex-shrink-0" />
                    {feature}
                  </li>
                ))}
              </ul>

              {/* CTA */}
              <button
                onClick={() => handleNavClick('#contact')}
                className="mt-auto font-sans text-xs font-bold text-gold uppercase tracking-wider flex items-center gap-1 group-hover:gap-2 transition-all duration-300"
              >
                Enquire Now
                <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                </svg>
              </button>
            </div>
          ))}
        </div>

        {/* Bottom CTA */}
        <div className="text-center mt-16 animate-on-scroll">
          <div className="bg-cream p-8 md:p-12 max-w-3xl mx-auto">
            <h3 className="font-serif text-2xl font-bold text-navy mb-3">
              Don&apos;t See Exactly What You&apos;re Looking For?
            </h3>
            <p className="font-sans text-gray-600 mb-6">
              Every tour can be fully customized. Contact David to design your perfect Morocco experience.
            </p>
            <div className="flex flex-wrap justify-center gap-4">
              <button
                onClick={() => handleNavClick('#contact')}
                className="btn-gold"
              >
                Request a Custom Itinerary
              </button>
              <a
                href="https://wa.me/212668702424?text=Hello%20David%2C%20I%20would%20like%20to%20discuss%20a%20custom%20tour."
                target="_blank"
                rel="noopener noreferrer"
                className="btn-outline-gold"
              >
                WhatsApp David
              </a>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
